// Firebase Emergency Cancellation Service
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  getDoc,
  getDocs,
  serverTimestamp,
  Timestamp,
  limit
} from 'firebase/firestore';
import { db } from '../../config/firebase';
import {
  EmergencyCancellationRequest,
  EmergencyCancellationNotification,
  CancellationRequestFormData,
  CancellationStatus,
  CancellationStats,
  CancellationRequestsCallback,
  CancellationNotificationsCallback,
  CancellationReasonCategory,
  UnsubscribeFunction
} from '../../types/emergencyCancellation';

class FirebaseEmergencyCancellationService {
  private static instance: FirebaseEmergencyCancellationService;
  private cancellationsCollection = 'emergencyCancellations';
  private notificationsCollection = 'emergencyCancellationNotifications';

  static getInstance(): FirebaseEmergencyCancellationService {
    if (!FirebaseEmergencyCancellationService.instance) {
      FirebaseEmergencyCancellationService.instance = new FirebaseEmergencyCancellationService();
    }
    return FirebaseEmergencyCancellationService.instance;
  }

  // ============ CLIENT METHODS ============

  /**
   * Submit an emergency cancellation request
   */
  async submitCancellationRequest(
    formData: CancellationRequestFormData,
    appointmentDetails: {
      appointmentDate: string;
      appointmentTime: string;
      appointmentType: 'video' | 'in-person';
      clientId: string;
      clientName: string;
      clientEmail?: string;
      clientPhone?: string;
      providerId: string;
      providerName: string;
    }
  ): Promise<string> {
    try {
      const requestData = {
        appointmentId: formData.appointmentId,
        appointmentDate: appointmentDetails.appointmentDate,
        appointmentTime: appointmentDetails.appointmentTime,
        appointmentType: appointmentDetails.appointmentType,
        clientId: appointmentDetails.clientId,
        clientName: appointmentDetails.clientName,
        clientEmail: appointmentDetails.clientEmail || null,
        clientPhone: appointmentDetails.clientPhone || null,
        providerId: appointmentDetails.providerId,
        providerName: appointmentDetails.providerName,
        urgency: formData.urgency,
        reasonCategory: formData.reasonCategory,
        reasonDetails: formData.reasonDetails,
        requestedAt: serverTimestamp(),
        wantsToReschedule: formData.wantsToReschedule,
        preferredRescheduleDate: formData.preferredRescheduleDate || null,
        preferredRescheduleTime: formData.preferredRescheduleTime || null,
        rescheduleNotes: formData.rescheduleNotes || null,
        status: 'pending',
        notificationSent: false,
        clientNotified: false
      };

      const docRef = await addDoc(
        collection(db, this.cancellationsCollection),
        requestData
      );

      // Create notification for provider
      await this.createProviderNotification(docRef.id, {
        providerId: appointmentDetails.providerId,
        clientName: appointmentDetails.clientName,
        appointmentDate: appointmentDetails.appointmentDate,
        appointmentTime: appointmentDetails.appointmentTime,
        urgency: formData.urgency,
        reasonCategory: formData.reasonCategory,
        reasonPreview: formData.reasonDetails.substring(0, 100)
      });

      return docRef.id;
    } catch (error) {
      console.error('Error submitting cancellation request:', error);
      throw error;
    }
  }

  /**
   * Get client's cancellation requests
   */
  async getClientCancellationRequests(clientId: string): Promise<EmergencyCancellationRequest[]> {
    try {
      const q = query(
        collection(db, this.cancellationsCollection),
        where('clientId', '==', clientId),
        orderBy('requestedAt', 'desc'),
        limit(20)
      );

      const snapshot = await getDocs(q);
      return snapshot.docs.map((doc) => this.convertToRequest(doc.id, doc.data()));
    } catch (error) {
      console.error('Error getting client cancellation requests:', error);
      return [];
    }
  }

  // ============ PROVIDER METHODS ============

  /**
   * Subscribe to pending cancellation requests for a provider
   */
  subscribeToPendingRequests(
    providerId: string,
    callback: CancellationRequestsCallback
  ): UnsubscribeFunction {
    const q = query(
      collection(db, this.cancellationsCollection),
      where('providerId', '==', providerId),
      where('status', 'in', ['pending', 'acknowledged']),
      orderBy('requestedAt', 'desc')
    );

    return onSnapshot(q, (snapshot) => {
      const requests = snapshot.docs.map((doc) =>
        this.convertToRequest(doc.id, doc.data())
      );
      callback(requests);
    }, (error) => {
      console.error('Error subscribing to pending requests:', error);
      callback([]);
    });
  }

  /**
   * Subscribe to all cancellation requests for a provider
   */
  subscribeToAllRequests(
    providerId: string,
    callback: CancellationRequestsCallback,
    limitCount: number = 50
  ): UnsubscribeFunction {
    const q = query(
      collection(db, this.cancellationsCollection),
      where('providerId', '==', providerId),
      orderBy('requestedAt', 'desc'),
      limit(limitCount)
    );

    return onSnapshot(q, (snapshot) => {
      const requests = snapshot.docs.map((doc) =>
        this.convertToRequest(doc.id, doc.data())
      );
      callback(requests);
    }, (error) => {
      console.error('Error subscribing to all requests:', error);
      callback([]);
    });
  }

  /**
   * Subscribe to cancellation notifications for a provider
   */
  subscribeToNotifications(
    providerId: string,
    callback: CancellationNotificationsCallback
  ): UnsubscribeFunction {
    const q = query(
      collection(db, this.notificationsCollection),
      where('providerId', '==', providerId),
      where('dismissed', '==', false),
      orderBy('timestamp', 'desc'),
      limit(20)
    );

    return onSnapshot(q, (snapshot) => {
      const notifications = snapshot.docs.map((doc) =>
        this.convertToNotification(doc.id, doc.data())
      );
      callback(notifications);
    }, (error) => {
      console.error('Error subscribing to notifications:', error);
      callback([]);
    });
  }

  /**
   * Acknowledge a cancellation request
   */
  async acknowledgeRequest(
    requestId: string,
    providerId: string,
    providerName: string
  ): Promise<void> {
    try {
      const docRef = doc(db, this.cancellationsCollection, requestId);
      await updateDoc(docRef, {
        status: 'acknowledged',
        acknowledgedAt: serverTimestamp(),
        acknowledgedBy: providerName
      });
    } catch (error) {
      console.error('Error acknowledging request:', error);
      throw error;
    }
  }

  /**
   * Approve a cancellation request
   */
  async approveRequest(
    requestId: string,
    providerName: string,
    notes?: string
  ): Promise<void> {
    try {
      const docRef = doc(db, this.cancellationsCollection, requestId);
      await updateDoc(docRef, {
        status: 'approved',
        processedAt: serverTimestamp(),
        processedBy: providerName,
        processingNotes: notes || null,
        clientNotified: true,
        clientNotifiedAt: serverTimestamp()
      });

      // Mark the associated notification as dismissed
      await this.dismissNotificationForRequest(requestId);
    } catch (error) {
      console.error('Error approving request:', error);
      throw error;
    }
  }

  /**
   * Reschedule appointment from cancellation request
   */
  async rescheduleFromRequest(
    requestId: string,
    providerName: string,
    newAppointment: {
      appointmentId: string;
      date: string;
      time: string;
    },
    notes?: string
  ): Promise<void> {
    try {
      const docRef = doc(db, this.cancellationsCollection, requestId);
      await updateDoc(docRef, {
        status: 'rescheduled',
        processedAt: serverTimestamp(),
        processedBy: providerName,
        processingNotes: notes || null,
        newAppointmentId: newAppointment.appointmentId,
        newAppointmentDate: newAppointment.date,
        newAppointmentTime: newAppointment.time,
        clientNotified: true,
        clientNotifiedAt: serverTimestamp()
      });

      // Mark the associated notification as dismissed
      await this.dismissNotificationForRequest(requestId);
    } catch (error) {
      console.error('Error rescheduling from request:', error);
      throw error;
    }
  }

  /**
   * Deny a cancellation request
   */
  async denyRequest(
    requestId: string,
    providerName: string,
    reason: string
  ): Promise<void> {
    try {
      const docRef = doc(db, this.cancellationsCollection, requestId);
      await updateDoc(docRef, {
        status: 'denied',
        processedAt: serverTimestamp(),
        processedBy: providerName,
        denialReason: reason,
        clientNotified: true,
        clientNotifiedAt: serverTimestamp()
      });

      // Mark the associated notification as dismissed
      await this.dismissNotificationForRequest(requestId);
    } catch (error) {
      console.error('Error denying request:', error);
      throw error;
    }
  }

  /**
   * Archive a resolved cancellation request
   */
  async archiveRequest(requestId: string): Promise<void> {
    try {
      const docRef = doc(db, this.cancellationsCollection, requestId);
      await updateDoc(docRef, {
        archived: true,
        archivedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error archiving request:', error);
      throw error;
    }
  }

  /**
   * Unarchive a cancellation request
   */
  async unarchiveRequest(requestId: string): Promise<void> {
    try {
      const docRef = doc(db, this.cancellationsCollection, requestId);
      await updateDoc(docRef, {
        archived: false,
        archivedAt: null
      });
    } catch (error) {
      console.error('Error unarchiving request:', error);
      throw error;
    }
  }

  /**
   * Get cancellation statistics for a provider
   */
  async getCancellationStats(providerId: string): Promise<CancellationStats> {
    try {
      // Get requests from this month
      const startOfMonth = new Date();
      startOfMonth.setDate(1);
      startOfMonth.setHours(0, 0, 0, 0);

      const q = query(
        collection(db, this.cancellationsCollection),
        where('providerId', '==', providerId),
        where('requestedAt', '>=', Timestamp.fromDate(startOfMonth))
      );

      const snapshot = await getDocs(q);
      const requests = snapshot.docs.map((doc) => doc.data());

      // Calculate stats
      const stats: CancellationStats = {
        totalThisMonth: requests.length,
        pendingRequests: requests.filter(r => r.status === 'pending' || r.status === 'acknowledged').length,
        byReason: {},
        rescheduledCount: requests.filter(r => r.status === 'rescheduled').length,
        averageResponseTime: 0
      };

      // Count by reason
      requests.forEach(r => {
        const reason = r.reasonCategory as CancellationReasonCategory;
        if (reason) {
          stats.byReason[reason] = (stats.byReason[reason] || 0) + 1;
        }
      });

      // Calculate average response time for processed requests
      const processedRequests = requests.filter(r =>
        r.processedAt && r.requestedAt
      );

      if (processedRequests.length > 0) {
        const totalResponseTime = processedRequests.reduce((sum, r) => {
          const requestedAt = this.timestampToDate(r.requestedAt);
          const processedAt = this.timestampToDate(r.processedAt);
          return sum + (processedAt.getTime() - requestedAt.getTime());
        }, 0);
        stats.averageResponseTime = Math.round(
          totalResponseTime / processedRequests.length / 60000
        ); // Convert to minutes
      }

      return stats;
    } catch (error) {
      console.error('Error getting cancellation stats:', error);
      return {
        totalThisMonth: 0,
        pendingRequests: 0,
        byReason: {},
        rescheduledCount: 0,
        averageResponseTime: 0
      };
    }
  }

  /**
   * Get pending cancellation count for a provider
   */
  async getPendingCount(providerId: string): Promise<number> {
    try {
      const q = query(
        collection(db, this.cancellationsCollection),
        where('providerId', '==', providerId),
        where('status', 'in', ['pending', 'acknowledged'])
      );

      const snapshot = await getDocs(q);
      return snapshot.size;
    } catch (error) {
      console.error('Error getting pending count:', error);
      return 0;
    }
  }

  /**
   * Subscribe to pending count
   */
  subscribeToPendingCount(
    providerId: string,
    callback: (count: number) => void
  ): UnsubscribeFunction {
    const q = query(
      collection(db, this.cancellationsCollection),
      where('providerId', '==', providerId),
      where('status', '==', 'pending')
    );

    return onSnapshot(q, (snapshot) => {
      callback(snapshot.size);
    }, (error) => {
      console.error('Error subscribing to pending count:', error);
      callback(0);
    });
  }

  // ============ NOTIFICATION METHODS ============

  /**
   * Create a provider notification
   */
  private async createProviderNotification(
    cancellationRequestId: string,
    details: {
      providerId: string;
      clientName: string;
      appointmentDate: string;
      appointmentTime: string;
      urgency: string;
      reasonCategory: string;
      reasonPreview: string;
    }
  ): Promise<void> {
    try {
      await addDoc(collection(db, this.notificationsCollection), {
        cancellationRequestId,
        providerId: details.providerId,
        clientName: details.clientName,
        appointmentDate: details.appointmentDate,
        appointmentTime: details.appointmentTime,
        urgency: details.urgency,
        reasonCategory: details.reasonCategory,
        reasonPreview: details.reasonPreview,
        timestamp: serverTimestamp(),
        read: false,
        dismissed: false
      });
    } catch (error) {
      console.error('Error creating provider notification:', error);
    }
  }

  /**
   * Mark notification as read
   */
  async markNotificationAsRead(notificationId: string): Promise<void> {
    try {
      const docRef = doc(db, this.notificationsCollection, notificationId);
      await updateDoc(docRef, {
        read: true,
        readAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  }

  /**
   * Dismiss a notification
   */
  async dismissNotification(notificationId: string): Promise<void> {
    try {
      const docRef = doc(db, this.notificationsCollection, notificationId);
      await updateDoc(docRef, {
        dismissed: true
      });
    } catch (error) {
      console.error('Error dismissing notification:', error);
    }
  }

  /**
   * Dismiss notification for a specific request
   */
  private async dismissNotificationForRequest(requestId: string): Promise<void> {
    try {
      const q = query(
        collection(db, this.notificationsCollection),
        where('cancellationRequestId', '==', requestId)
      );

      const snapshot = await getDocs(q);
      const updates = snapshot.docs.map((docSnap) =>
        updateDoc(docSnap.ref, { dismissed: true })
      );

      await Promise.all(updates);
    } catch (error) {
      console.error('Error dismissing notification for request:', error);
    }
  }

  // ============ CONVERSION HELPERS ============

  private convertToRequest(id: string, data: any): EmergencyCancellationRequest {
    return {
      id,
      appointmentId: data.appointmentId,
      appointmentDate: data.appointmentDate,
      appointmentTime: data.appointmentTime,
      appointmentType: data.appointmentType,
      clientId: data.clientId,
      clientName: data.clientName,
      clientEmail: data.clientEmail,
      clientPhone: data.clientPhone,
      providerId: data.providerId,
      providerName: data.providerName,
      urgency: data.urgency,
      reasonCategory: data.reasonCategory,
      reasonDetails: data.reasonDetails,
      requestedAt: this.timestampToDate(data.requestedAt),
      wantsToReschedule: data.wantsToReschedule,
      preferredRescheduleDate: data.preferredRescheduleDate,
      preferredRescheduleTime: data.preferredRescheduleTime,
      rescheduleNotes: data.rescheduleNotes,
      status: data.status,
      acknowledgedAt: data.acknowledgedAt ? this.timestampToDate(data.acknowledgedAt) : undefined,
      acknowledgedBy: data.acknowledgedBy,
      processedAt: data.processedAt ? this.timestampToDate(data.processedAt) : undefined,
      processedBy: data.processedBy,
      processingNotes: data.processingNotes,
      newAppointmentId: data.newAppointmentId,
      newAppointmentDate: data.newAppointmentDate,
      newAppointmentTime: data.newAppointmentTime,
      denialReason: data.denialReason,
      notificationSent: data.notificationSent,
      notificationSentAt: data.notificationSentAt ? this.timestampToDate(data.notificationSentAt) : undefined,
      clientNotified: data.clientNotified,
      clientNotifiedAt: data.clientNotifiedAt ? this.timestampToDate(data.clientNotifiedAt) : undefined,
      archived: data.archived || false,
      archivedAt: data.archivedAt ? this.timestampToDate(data.archivedAt) : undefined
    };
  }

  private convertToNotification(id: string, data: any): EmergencyCancellationNotification {
    return {
      id,
      cancellationRequestId: data.cancellationRequestId,
      providerId: data.providerId,
      clientName: data.clientName,
      appointmentDate: data.appointmentDate,
      appointmentTime: data.appointmentTime,
      urgency: data.urgency,
      reasonCategory: data.reasonCategory,
      reasonPreview: data.reasonPreview,
      timestamp: this.timestampToDate(data.timestamp),
      read: data.read || false,
      readAt: data.readAt ? this.timestampToDate(data.readAt) : undefined,
      dismissed: data.dismissed || false
    };
  }

  private timestampToDate(timestamp: Timestamp | Date | null): Date {
    if (!timestamp) return new Date();
    if (timestamp instanceof Timestamp) {
      return timestamp.toDate();
    }
    return timestamp;
  }
}

export default FirebaseEmergencyCancellationService;
